import { useState, type FC, useEffect, type Dispatch, type SetStateAction } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type PreparePaymentRequest, preparePayment, type SecurePayAccessCode } from '@/api/registration/payment';
import { type ApiResponse } from '@/api/tenantClient';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { SkeletonText } from '@chakra-ui/react';
import PayBySecurePay from './PayBySecurePay';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import { cartQueryKey } from '@/api/registration';

export interface PayBySecurePayWrapperProps {
  setErrors: Dispatch<SetStateAction<string[]>>
  isValid: boolean
}

const PayBySecurePayWrapper: FC<PayBySecurePayWrapperProps> = ({
  setErrors,
  isValid,
  ...props
}) => {
  const [options, setOptions] = useState<SecurePayAccessCode>();
  const { createTenantApiRequest } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const queryClient = useQueryClient();

  const { data: cart } = useGroupCartQuery(eventId);

  const { mutate: preparePaymentMutate, isLoading } = useMutation<ApiResponse<SecurePayAccessCode>, Error, PreparePaymentRequest>({
    mutationFn: async (preparePaymentData: PreparePaymentRequest) => {
      return await preparePayment(createTenantApiRequest)(preparePaymentData) as ApiResponse<SecurePayAccessCode>;
    },
    onSuccess: (result) => {
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        setOptions(result.items[0]);
        void queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId: cart?.registrationId }] });
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to save the host.']);
    }
  });

  useEffect(() => {
    if (cart) {
      const preparePaymentData: PreparePaymentRequest = {
        cartId: cart.id,
        locale: 'aud',
        gateway: 'secure_pay'
      };
      setErrors([]);
      preparePaymentMutate(preparePaymentData);
    }
  }, [cart]);

  return isLoading ? (
    <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
  )
    : options?.merchantCode && (
      <PayBySecurePay
        setErrors={setErrors}
        isValid={isValid}
        options={options}
      />
    );
};

export default PayBySecurePayWrapper;
